import type { FeuilleExport } from './classeur'
import type { FichierLu } from './lecture'
import type { DemandeTableur, ReponseTableur } from './tableur.worker'

// Lecture et écriture des classeurs dans un Web Worker : l'interface reste
// fluide sur 20 000 lignes. Un seul worker, créé à la première demande.

type Demande = DemandeTableur extends infer D ? (D extends unknown ? Omit<D, 'id'> : never) : never

let worker: Worker | null = null
let prochainId = 1
const enCours = new Map<number, { resoudre: (r: ReponseTableur) => void; rejeter: (e: Error) => void }>()

function obtenirWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./tableur.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = ({ data }: MessageEvent<ReponseTableur>) => {
      const attente = enCours.get(data.id)
      enCours.delete(data.id)
      if (!attente) return
      if ('erreur' in data) {
        attente.rejeter(new Error(data.lisible ? data.erreur : `Lecture impossible (${data.erreur})`))
      } else attente.resoudre(data)
    }
    worker.onerror = (e) => {
      for (const { rejeter } of enCours.values()) rejeter(new Error(e.message || 'worker tableur indisponible'))
      enCours.clear()
      worker?.terminate()
      worker = null
    }
  }
  return worker
}

function demander(demande: Demande, transfert: Transferable[] = []): Promise<ReponseTableur> {
  return new Promise((resoudre, rejeter) => {
    const id = prochainId++
    enCours.set(id, { resoudre, rejeter })
    obtenirWorker().postMessage({ id, ...demande } as DemandeTableur, transfert)
  })
}

export async function lireFichierTableur(fichier: File): Promise<FichierLu> {
  const octets = await fichier.arrayBuffer()
  const r = await demander({ type: 'lire', nom: fichier.name, octets }, [octets])
  return (r as { fichier: FichierLu }).fichier
}

export async function classeurXlsx(feuilles: FeuilleExport[]): Promise<Blob> {
  const r = await demander({ type: 'ecrire', feuilles })
  return new Blob([(r as { classeur: ArrayBuffer }).classeur], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

/** Téléchargement d'un fichier généré sur l'appareil. */
export function telecharger(contenu: Blob, nom: string) {
  const url = URL.createObjectURL(contenu)
  const lien = Object.assign(document.createElement('a'), { href: url, download: nom })
  document.body.append(lien)
  lien.click()
  lien.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export const csvBlob = (csv: string) => new Blob([csv], { type: 'text/csv;charset=utf-8' })
